import { useRef, useState } from 'react';
import { gridCommandForKey } from './keyboardCommands';
import { parseTsv, serializeTsv } from './clipboardTsv';
import { planPaste } from './pastePlan';
import { fillToEndTarget, fillValueAt, planFill } from './fillPattern';

const DEFAULT_READ_ONLY_HINT = "These cells can't be edited.";
const CLIPBOARD_BLOCKED_HINT = 'The browser blocked clipboard access.';
const FOCUSABLE_CELL_CONTENT = 'button, a[href], input, select, textarea, [tabindex]';

function countLabel(count, noun) {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function isSameCell(left, right) {
    return Boolean(left && right && left.rowId === right.rowId && left.columnId === right.columnId);
}

// Buttons, links and inputs inside a cell keep their own clicks and focus.
function targetsCellControl(target, cellElement) {
    const control = target?.closest?.(FOCUSABLE_CELL_CONTENT);
    return Boolean(control && control !== cellElement && cellElement.contains(control));
}

function writeClipboardText(text) {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        return Promise.reject(new Error('Clipboard unavailable'));
    }
    return navigator.clipboard.writeText(text);
}

function readClipboardText() {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
        return Promise.reject(new Error('Clipboard unavailable'));
    }
    return navigator.clipboard.readText();
}

/**
 * Spreadsheet editing for a grid: keyboard navigation, the cell editor,
 * clear / fill / clipboard / undo commands and their feedback.
 *
 * `config` is the owner's draft store:
 * - isCellEditable(rowId, columnId)
 * - applyEdits(edits, label) -> the recorded history action ({ changes }) or
 *   null when nothing changed; `edits` are [{ rowId, columnId, value }] and
 *   one call is one undo step
 * - undo(expectedAction?) / redo() -> the action they reversed, or null
 * - getCellEditorProps(rowId, columnId) -> { label, inputLabel, options }
 * - readOnlyHint (optional): shown when a command reaches no editable cell
 * - interceptPaste(rows) (optional): sees each pasted block (string[][])
 *   before the cell paste; returning true takes the paste over and leaves
 *   the cells as they were
 *
 * `view` is the grid's DOM side: scrolling, focus and cell lookup;
 * navigatePastEnd() (optional) takes ArrowDown from the last row, and Tab
 * from its last cell, and returns true when it moved focus there.
 */
export default function useSpreadsheetEditing({
    config,
    selection,
    rowIds,
    columnIds,
    isMac,
    view,
}) {
    const enabled = Boolean(config);
    const [editor, setEditor] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const pointerRef = useRef(null);
    const sequenceRef = useRef(0);

    // An open editor closes once its cell stops being editable (a save or a
    // refresh starts, or the row leaves the list).
    const editorCellEditable = Boolean(
        editor && enabled && config.isCellEditable(editor.rowId, editor.columnId),
    );
    if (editor && !editorCellEditable) setEditor(null);

    const nextSequence = () => {
        sequenceRef.current += 1;
        return sequenceRef.current;
    };
    const notify = (message, undoAction = null) => {
        setFeedback({ key: nextSequence(), message, undoAction });
    };
    const readOnlyHint = () => notify(config.readOnlyHint || DEFAULT_READ_ONLY_HINT);
    const { readCellText } = view;
    const isEditable = (rowId, columnId) => (
        selection.isSelectableColumn(columnId) && config.isCellEditable(rowId, columnId)
    );
    const editableCellsIn = (bounds) => selection.cellsIn(bounds)
        .filter((cell) => config.isCellEditable(cell.rowId, cell.columnId));

    // One owner call per user action, so each action is one undo step. A new
    // edit retires the previous toast: its Undo would no longer be the latest.
    const applyEdits = (edits, label) => {
        const action = edits.length ? config.applyEdits(edits, label) : null;
        nextSequence();
        setFeedback(null);
        return { action, changed: action?.changes?.length ?? 0 };
    };
    const reportMultiCellEdit = ({ action, changed }, message) => {
        if (action && changed > 1) notify(message, action);
    };

    const selectAndReveal = (anchor, focus = anchor) => {
        selection.select(anchor, focus);
        view.scrollToCell(focus.rowIndex, focus.columnIndex);
    };
    const selectFirstVisibleCell = () => {
        if (!selection.rowCount || selection.firstColumnIndex == null) return;
        const rowIndex = selection.clampRowIndex(view.getFirstVisibleRowIndex());
        selectAndReveal(selection.cellAt(rowIndex, selection.firstColumnIndex));
    };

    const openEditor = (cell, initialInput = null) => {
        if (isSameCell(editor, cell)) return;
        view.scrollToCell(cell.rowIndex, cell.columnIndex);
        const tryOpen = () => {
            const anchorEl = view.getCellElement(cell.rowId, cell.columnId);
            if (!anchorEl) return false;
            setEditor({
                key: nextSequence(),
                rowId: cell.rowId,
                columnId: cell.columnId,
                initialInput,
                value: readCellText(cell.rowId, cell.columnId),
                anchorEl,
                anchorRect: anchorEl.getBoundingClientRect(),
            });
            return true;
        };
        if (tryOpen()) return;
        // A row scrolled out of the rendered window appears after the
        // virtualizer's next update.
        let attempts = 0;
        const retry = () => {
            attempts += 1;
            if (!tryOpen() && attempts < 4) window.requestAnimationFrame(retry);
        };
        window.requestAnimationFrame(retry);
    };

    const editActiveCell = (initialInput) => {
        const active = selection.getActiveCell();
        if (!active) {
            selectFirstVisibleCell();
            return;
        }
        if (!config.isCellEditable(active.rowId, active.columnId)) {
            readOnlyHint();
            return;
        }
        openEditor(active, initialInput);
    };

    const fillSelectionWith = (value, editedCell) => {
        const cells = editableCellsIn(selection.getBounds());
        if (!cells.some((cell) => isSameCell(cell, editedCell))) cells.unshift(editedCell);
        const result = applyEdits(cells.map((cell) => ({ ...cell, value })), 'Fill selection');
        reportMultiCellEdit(result, `Updated ${countLabel(result.changed, 'cell')}`);
    };

    const commitEditor = (value, command) => {
        const edited = editor;
        if (!edited) return;
        setEditor(null);
        if (!enabled) return;
        const cell = { rowId: edited.rowId, columnId: edited.columnId };
        if (command?.type === 'commitToSelection') {
            fillSelectionWith(value, cell);
            view.focusGrid();
            return;
        }
        applyEdits([{ ...cell, value }], 'Edit');
        // A blur (the user clicked elsewhere) commits without moving or
        // taking focus back.
        if (!command) return;
        const position = selection.locate(cell.rowId, cell.columnId);
        if (position && (command.rowDelta || command.columnDelta)) {
            const rowIndex = selection.clampRowIndex(position.rowIndex + command.rowDelta);
            const columnIndex = command.columnDelta
                ? (selection.stepColumn(position.columnIndex, command.columnDelta) ?? position.columnIndex)
                : position.columnIndex;
            selectAndReveal(selection.cellAt(rowIndex, columnIndex));
        }
        view.focusGrid();
    };

    const cancelEditor = () => {
        setEditor(null);
        view.focusGrid();
    };

    const selectionText = (bounds) => {
        const lines = [];
        for (let rowIndex = bounds.minRowIndex; rowIndex <= bounds.maxRowIndex; rowIndex += 1) {
            const cells = [];
            for (let columnIndex = bounds.minColumnIndex; columnIndex <= bounds.maxColumnIndex; columnIndex += 1) {
                cells.push(selection.isSelectableColumnIndex(columnIndex)
                    ? readCellText(rowIds[rowIndex], columnIds[columnIndex])
                    : '');
            }
            lines.push(cells);
        }
        return serializeTsv(lines);
    };

    const copySelection = () => {
        const bounds = selection.getBounds();
        if (!bounds) return;
        const count = selection.cellsIn(bounds).length;
        // Confirm only if nothing newer has been reported meanwhile.
        const copyRequest = nextSequence();
        writeClipboardText(selectionText(bounds)).then(
            () => {
                if (sequenceRef.current === copyRequest) notify(`Copied ${countLabel(count, 'cell')}`);
            },
            () => notify(`Couldn't copy. ${CLIPBOARD_BLOCKED_HINT}`),
        );
    };

    const clearSelection = () => {
        const cells = editableCellsIn(selection.getBounds());
        if (!cells.length) {
            readOnlyHint();
            return;
        }
        const result = applyEdits(cells.map((cell) => ({ ...cell, value: '' })), 'Clear');
        reportMultiCellEdit(result, `Cleared ${countLabel(result.changed, 'cell')}`);
    };

    const cutSelection = () => {
        const bounds = selection.getBounds();
        if (!bounds) return;
        writeClipboardText(selectionText(bounds))
            .catch(() => notify(`Couldn't copy. ${CLIPBOARD_BLOCKED_HINT}`));
        const cells = editableCellsIn(bounds);
        if (!cells.length) {
            notify(`Copied only. ${config.readOnlyHint || DEFAULT_READ_ONLY_HINT}`);
            return;
        }
        const result = applyEdits(cells.map((cell) => ({ ...cell, value: '' })), 'Cut');
        reportMultiCellEdit(result, `Cut ${countLabel(result.changed, 'cell')}`);
    };

    const pasteText = (text) => {
        const block = parseTsv(text);
        if (!block.length || config.interceptPaste?.(block)) return;
        const bounds = selection.getBounds();
        if (!bounds) return;
        const { changes, clippedCount } = planPaste({
            rowIds,
            columnIds,
            selection: bounds,
            block,
            isEditable,
        });
        const clipped = clippedCount ? `${countLabel(clippedCount, 'value')} didn't fit` : '';
        if (!changes.length) {
            if (clipped) notify(`Nothing pasted: ${clipped}`);
            else readOnlyHint();
            return;
        }
        const result = applyEdits(changes, 'Paste');
        const singleValue = block.length === 1 && block[0].length === 1;
        if (!singleValue && selection.lastColumnIndex != null) {
            // Select what was pasted, like a spreadsheet.
            const width = block.reduce((widest, cells) => Math.max(widest, cells.length), 0);
            const lastRow = selection.clampRowIndex(bounds.minRowIndex + block.length - 1);
            const lastColumn = Math.min(selection.lastColumnIndex, bounds.minColumnIndex + width - 1);
            selection.select(
                selection.cellAt(bounds.minRowIndex, bounds.minColumnIndex),
                selection.cellAt(lastRow, Math.max(bounds.minColumnIndex, lastColumn)),
            );
        }
        if (result.action && (result.changed > 1 || clipped)) {
            notify(`Pasted ${countLabel(result.changed, 'value')}${clipped ? ` · ${clipped}` : ''}`, result.action);
        } else if (clipped) {
            notify(clipped);
        }
    };

    // The shortcut's own paste needs no clipboard permission, so the grid
    // lets the browser paste into a hidden field first; the clipboard API
    // (which may ask for permission) is the fallback.
    const requestPaste = () => {
        view.capturePaste((text) => {
            if (text != null) {
                pasteText(text);
                return;
            }
            readClipboardText().then(
                (clipboardText) => pasteText(clipboardText),
                () => notify(`Couldn't paste. ${CLIPBOARD_BLOCKED_HINT}`),
            );
        });
    };

    const fillDown = () => {
        const bounds = selection.getBounds();
        if (!bounds) return;
        let sourceIndex = bounds.minRowIndex;
        let targetStart = bounds.minRowIndex + 1;
        if (bounds.maxRowIndex === bounds.minRowIndex) {
            // One row selected: copy the row above into it, as Excel does.
            sourceIndex = bounds.minRowIndex - 1;
            targetStart = bounds.minRowIndex;
        }
        if (sourceIndex < 0) {
            notify('Select the value to copy and the cells below it.');
            return;
        }
        const columns = [];
        for (let index = bounds.minColumnIndex; index <= bounds.maxColumnIndex; index += 1) {
            if (selection.isSelectableColumnIndex(index)) columns.push(columnIds[index]);
        }
        const changes = planFill({
            rowIds,
            columnIds: columns,
            sourceRange: { startIndex: sourceIndex, endIndex: sourceIndex },
            targetRange: { startIndex: targetStart, endIndex: bounds.maxRowIndex },
            readValue: readCellText,
            isEditable,
        });
        if (!changes.length) {
            readOnlyHint();
            return;
        }
        const result = applyEdits(changes, 'Fill down');
        reportMultiCellEdit(result, `Filled ${countLabel(result.changed, 'cell')}`);
    };

    // The fill handle copies the selection's rows across its columns; rows
    // are display indexes (current sort and filter), as useFillDrag reports
    // them, so rows the user cannot see are never written.
    const selectedColumnIds = (bounds) => {
        const ids = [];
        for (let index = bounds.minColumnIndex; index <= bounds.maxColumnIndex; index += 1) {
            if (selection.isSelectableColumnIndex(index)) ids.push(columnIds[index]);
        }
        return ids;
    };
    const fillFromHandle = (sourceRange, targetRange) => {
        const bounds = selection.getBounds();
        if (!bounds || !targetRange || targetRange.startIndex > targetRange.endIndex) return;
        const changes = planFill({
            rowIds,
            columnIds: selectedColumnIds(bounds),
            sourceRange,
            targetRange,
            readValue: readCellText,
            isEditable,
        });
        view.focusGrid();
        if (!changes.length) {
            readOnlyHint();
            return;
        }
        const result = applyEdits(changes, 'Fill');
        reportMultiCellEdit(result, `Filled ${countLabel(result.changed, 'cell')}`);
        // Select what the fill covered, source included, as Excel does; the
        // active cell stays on the source side.
        const active = selection.getActiveCell();
        const focus = selection.getFocusCell() || active;
        const down = targetRange.startIndex > sourceRange.endIndex;
        selection.select(
            selection.cellAt(down ? sourceRange.startIndex : sourceRange.endIndex, active.columnIndex),
            selection.cellAt(
                selection.clampRowIndex(down ? targetRange.endIndex : targetRange.startIndex),
                focus.columnIndex,
            ),
        );
    };

    const undo = () => {
        setFeedback(null);
        if (!config.undo?.()) notify('Nothing to undo');
    };
    const redo = () => {
        setFeedback(null);
        if (!config.redo?.()) notify('Nothing to redo');
    };
    const undoFromToast = (action) => {
        setFeedback(null);
        if (!config.undo?.(action)) notify('That change was already undone or followed by another edit.');
    };

    const move = (command) => {
        const active = selection.getActiveCell();
        if (!active) {
            selectFirstVisibleCell();
            return;
        }
        if (
            command.rowDelta < 0 && !command.extend && !command.toEdge
            && active.rowIndex === 0 && view.focusHeader(active.columnId)
        ) return;
        // Down from the last row continues into the grid's add row, if any.
        if (
            command.rowDelta > 0 && !command.extend && !command.toEdge
            && active.rowIndex === selection.rowCount - 1 && view.navigatePastEnd?.()
        ) return;
        const origin = command.extend ? (selection.getFocusCell() || active) : active;
        let { rowIndex, columnIndex } = origin;
        if (command.rowDelta) {
            rowIndex = command.toEdge
                ? (command.rowDelta < 0 ? 0 : selection.rowCount - 1)
                : selection.clampRowIndex(rowIndex + command.rowDelta);
        }
        if (command.columnDelta) {
            columnIndex = command.toEdge
                ? (command.columnDelta < 0 ? selection.firstColumnIndex : selection.lastColumnIndex)
                : (selection.stepColumn(columnIndex, command.columnDelta) ?? columnIndex);
        }
        const target = selection.cellAt(rowIndex, columnIndex);
        selectAndReveal(command.extend ? active : target, target);
    };

    // Tab moves across a row; past the first or last column, focus leaves the
    // grid. From the last cell it continues into the grid's add row, if any.
    const tab = (delta) => {
        const active = selection.getActiveCell();
        if (!active) {
            selectFirstVisibleCell();
            return true;
        }
        const columnIndex = selection.stepColumn(active.columnIndex, delta);
        if (columnIndex == null) {
            if (delta > 0 && active.rowIndex === selection.rowCount - 1 && view.navigatePastEnd?.()) return true;
            return view.focusOutside(delta);
        }
        selectAndReveal(selection.cellAt(active.rowIndex, columnIndex));
        return true;
    };

    const jump = (command) => {
        const active = selection.getActiveCell();
        if (!active) {
            selectFirstVisibleCell();
            return;
        }
        const origin = command.extend ? (selection.getFocusCell() || active) : active;
        const toStart = command.type === 'home';
        const columnIndex = toStart ? selection.firstColumnIndex : selection.lastColumnIndex;
        const rowIndex = command.toGridCorner ? (toStart ? 0 : selection.rowCount - 1) : origin.rowIndex;
        const target = selection.cellAt(rowIndex, columnIndex);
        selectAndReveal(command.extend ? active : target, target);
    };

    const page = (command) => {
        const active = selection.getActiveCell();
        if (!active) {
            selectFirstVisibleCell();
            return;
        }
        const origin = command.extend ? (selection.getFocusCell() || active) : active;
        const rowIndex = selection.clampRowIndex(origin.rowIndex + (command.direction * view.getPageRowCount()));
        const target = selection.cellAt(rowIndex, origin.columnIndex);
        selectAndReveal(command.extend ? active : target, target);
    };

    // Returns false to let the browser keep the key's default behaviour.
    const runCommand = (command, event) => {
        switch (command.type) {
            case 'move':
                if (event.key === 'Tab') return tab(command.columnDelta);
                move(command);
                return true;
            case 'home':
            case 'end':
                jump(command);
                return true;
            case 'page':
                page(command);
                return true;
            case 'edit':
                editActiveCell(null);
                return true;
            case 'typeCharacter':
                editActiveCell(command.character);
                return true;
            case 'clear':
                clearSelection();
                return true;
            case 'collapseSelection':
                return selection.collapse();
            case 'undo':
                undo();
                return true;
            case 'redo':
                redo();
                return true;
            case 'copy':
                copySelection();
                return true;
            case 'cut':
                cutSelection();
                return true;
            case 'paste':
                requestPaste();
                return false;
            case 'fillDown':
                fillDown();
                return true;
            case 'selectAll':
                selection.selectAll();
                return true;
            default:
                return false;
        }
    };

    const cellFromEvent = (event) => {
        const element = event.target?.closest?.('[role="gridcell"]');
        if (!element || !event.currentTarget.contains(element)) return null;
        const rowId = element.closest('[role="row"]')?.getAttribute('data-id');
        const position = selection.locate(rowId, element.getAttribute('data-field'));
        if (!position || !selection.isSelectableColumnIndex(position.columnIndex)) return null;
        return { ...position, element };
    };

    const gridProps = {
        tabIndex: 0,
        'aria-multiselectable': true,
        onKeyDown: (event) => {
            // Keys typed into header cells or controls inside cells are theirs.
            if (event.target !== event.currentTarget || event.nativeEvent?.isComposing) return;
            const command = gridCommandForKey(event, { isMac });
            if (command && runCommand(command, event) !== false) event.preventDefault();
        },
        onFocus: (event) => {
            if (event.target === event.currentTarget && !selection.getActiveCell()) selectFirstVisibleCell();
        },
        onMouseDown: (event) => {
            if (event.button !== 0) return;
            const cell = cellFromEvent(event);
            if (!cell) return;
            const active = selection.getActiveCell();
            pointerRef.current = {
                rowId: cell.rowId,
                columnId: cell.columnId,
                wasActive: isSameCell(active, cell) && selection.isSingleCell() && !event.shiftKey,
            };
            selection.startPointerSelection(cell.rowId, cell.columnId, event);
            if (event.defaultPrevented || !targetsCellControl(event.target, cell.element)) view.focusGrid();
        },
        onMouseOver: (event) => {
            const cell = cellFromEvent(event);
            if (cell) selection.extendPointerSelection(cell.rowId, cell.columnId, event);
        },
        onClick: (event) => {
            const pointer = pointerRef.current;
            pointerRef.current = null;
            const cell = cellFromEvent(event);
            if (!cell || event.shiftKey || targetsCellControl(event.target, cell.element)) return;
            if (isSameCell(pointer, cell)) {
                if (!pointer.wasActive) return;
            } else if (!isSameCell(selection.getActiveCell(), cell) || !selection.isSingleCell()) {
                // A click without a pointer press (assistive technology, scripts)
                // selects first, like a mouse click.
                selection.select(cell);
                view.focusGrid();
                return;
            }
            if (selection.isSingleCell() && config.isCellEditable(cell.rowId, cell.columnId)) openEditor(cell);
        },
        onDoubleClick: (event) => {
            const cell = cellFromEvent(event);
            if (!cell || targetsCellControl(event.target, cell.element)) return;
            if (!config.isCellEditable(cell.rowId, cell.columnId)) return;
            selection.select(cell);
            openEditor(cell);
        },
    };

    return {
        gridProps: enabled ? gridProps : null,
        editor: editor && editorCellEditable ? {
            ...editor,
            ...config.getCellEditorProps?.(editor.rowId, editor.columnId),
            onCommit: commitEditor,
            onCancel: cancelEditor,
        } : null,
        feedback: feedback ? {
            key: feedback.key,
            message: feedback.message,
            onUndo: feedback.undoAction ? () => undoFromToast(feedback.undoAction) : null,
        } : null,
        closeFeedback: () => setFeedback(null),
        /** Header ArrowDown: continue into the first row at that column. */
        enterBodyAt: (columnId) => {
            if (!enabled || !selection.rowCount || selection.firstColumnIndex == null) return false;
            const columnIndex = selection.columnIndexOf(columnId);
            const target = selection.cellAt(
                0,
                selection.isSelectableColumnIndex(columnIndex) ? columnIndex : selection.firstColumnIndex,
            );
            selectAndReveal(target);
            view.focusGrid();
            return true;
        },
        /** Up from the grid's add row: the last row, in the active cell's column. */
        enterLastRow: () => {
            if (!enabled || !selection.rowCount || selection.firstColumnIndex == null) return false;
            const active = selection.getActiveCell();
            selectAndReveal(selection.cellAt(
                selection.rowCount - 1,
                active ? active.columnIndex : selection.firstColumnIndex,
            ));
            view.focusGrid();
            return true;
        },
        collapseSelection: () => (enabled ? selection.collapse() : false),
        /**
         * The fill handle's side of a drag; ranges are useFillDrag's display
         * row ranges and columns are the selection's.
         * - canFill(bounds): the range holds at least one editable cell
         * - preview(sourceRange, targetRange): the target rows and
         *   valueAt(rowId, rowIndex, columnId), what a target cell would get
         *   (undefined where the fill skips it)
         * - commit(sourceRange, targetRange), fillToEnd(sourceRange): fill as
         *   one undo step, then select source and target
         */
        fill: {
            canFill: (bounds) => {
                if (!enabled || !bounds) return false;
                for (let columnIndex = bounds.minColumnIndex; columnIndex <= bounds.maxColumnIndex; columnIndex += 1) {
                    if (!selection.isSelectableColumnIndex(columnIndex)) continue;
                    for (let rowIndex = bounds.minRowIndex; rowIndex <= bounds.maxRowIndex; rowIndex += 1) {
                        if (config.isCellEditable(rowIds[rowIndex], columnIds[columnIndex])) return true;
                    }
                }
                return false;
            },
            preview: (sourceRange, targetRange) => {
                const bounds = selection.getBounds();
                if (!enabled || !bounds) return null;
                const sourceValues = new Map(selectedColumnIds(bounds).map((columnId) => {
                    const values = [];
                    for (let index = sourceRange.startIndex; index <= sourceRange.endIndex; index += 1) {
                        values.push(readCellText(rowIds[index], columnId));
                    }
                    return [columnId, values];
                }));
                return {
                    key: `${sourceRange.startIndex}-${sourceRange.endIndex}:${bounds.minColumnIndex}-${bounds.maxColumnIndex}`,
                    startIndex: targetRange.startIndex,
                    endIndex: targetRange.endIndex,
                    valueAt: (rowId, rowIndex, columnId) => (
                        sourceValues.has(columnId) && isEditable(rowId, columnId)
                            ? fillValueAt(sourceValues.get(columnId), sourceRange.startIndex, rowIndex)
                            : undefined
                    ),
                };
            },
            commit: fillFromHandle,
            fillToEnd: (sourceRange) => fillFromHandle(sourceRange, fillToEndTarget(sourceRange, rowIds.length)),
        },
        /**
         * For tools outside the grid. applyEdits, for a category rename:
         * apply edits as one undo step and confirm them with the Undo toast,
         * worded by describe(changedCount); returns the history action or
         * null. paste, for a paste elsewhere on the page: paste the text into
         * the selection as Mod+V does and focus the grid; false when no cell
         * is selected.
         */
        commands: enabled ? {
            applyEdits: (edits, label, describe) => {
                const result = applyEdits(edits, label);
                if (result.action) notify(describe(result.changed), result.action);
                return result.action;
            },
            paste: (text) => {
                if (!selection.getBounds()) return false;
                pasteText(text);
                view.focusGrid();
                return true;
            },
        } : null,
    };
}
